import { useTranslation } from 'react-i18next';
import QtyStepper from '@/components/ui/QtyStepper';
import Price from '@/components/ui/Price';
import {
  useUpdateSharedCartLineQtyMutation,
  useRemoveSharedCartLineMutation,
} from '@/features/tableSession/sharedCartApi';
import type { ResolvedSharedLine } from '@/features/tableSession/resolveSharedCart';

interface Props {
  line: ResolvedSharedLine;
  guestToken: string;
  /** SC-427: show the line price (ordering dine-in). Buffet is price-less. */
  showPrice?: boolean;
}

// SC-169: one row of the shared table cart, rendered under its guest's group
// header (attribution lives on the header, not the row). Qty/remove edit the
// server-side cart directly (the 5s poll then reflects it for the whole table).
// SC-427: the line price is shown in ordering mode so the table sees the amount
// before sending; buffet stays price-less (showPrice=false).
export default function SharedCartLineItem({ line, guestToken, showPrice = false }: Props) {
  const { t } = useTranslation();
  const [updateQty] = useUpdateSharedCartLineQtyMutation();
  const [removeLine] = useRemoveSharedCartLineMutation();

  const onQty = (qty: number) => {
    if (qty <= 0) {
      removeLine({ guestToken, lineId: line.lineId });
    } else {
      updateQty({ guestToken, lineId: line.lineId, quantity: qty });
    }
  };

  return (
    <div className="flex flex-col gap-0.5 py-2 border-b-[1px] border-ink/30">
      <div className="flex items-start justify-between gap-2">
        <h4 className="font-display text-ink leading-none m-0">{line.name}</h4>
        <button
          type="button"
          onClick={() => removeLine({ guestToken, lineId: line.lineId })}
          className="shrink-0 w-6 h-6 flex items-center justify-center bg-hot-pink text-[var(--on-accent)] border-[1px] border-ink text-sm font-mono leading-none"
          aria-label={t('cart.removeItem')}
        >×</button>
      </div>
      {line.optionLabels.length > 0 && (
        <ul className="list-none pl-0 m-0 font-mono text-xs text-ink/70 leading-tight">
          {line.optionLabels.map((label, i) => (
            <li key={i}>· {label}</li>
          ))}
        </ul>
      )}
      {line.comment && (
        <p className="m-0 font-mono text-xs italic text-ink/60">“{line.comment}”</p>
      )}
      <div className="flex items-center justify-between">
        <QtyStepper qty={line.qty} min={0} size="sm" onChange={onQty} />
        {showPrice && (
          <span className="font-display text-ink">
            <Price amountCents={line.unitPrice * line.qty} />
          </span>
        )}
      </div>
    </div>
  );
}
